import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Tag, Button, Space, message, Modal, Form, Input, Select, Switch, Popconfirm, Row, Col } from 'antd';
import { SafetyOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminApi, inventoryApi } from '../../../api/client';

export function BusinessRulesTab() {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<any>(null);
    const [locations, setLocations] = useState<any[]>([]);
    const [form] = Form.useForm();

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await inventoryApi.getLocations();
                setLocations(res.data || []);
            } catch (e) {
                console.error('Failed to fetch locations', e);
            }
        };
        fetchLocations();
    }, []);

    const { data: rules, isLoading } = useQuery({
        queryKey: ['businessRules'],
        queryFn: async () => (await adminApi.getBusinessRules()).data,
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateBusinessRule(id, data),
        onSuccess: () => {
            message.success('Reguła zaktualizowana');
            queryClient.invalidateQueries({ queryKey: ['businessRules'] });
            setIsModalOpen(false);
            setEditingRule(null);
        },
    });

    const handleOpenModal = (rule: any) => {
        setEditingRule(rule);

        let valueField = {};
        if (rule.code === 'FEFO_THRESHOLDS') {
            const thresholds = rule.value || { info: 60, warning: 30, critical: 14 };
            valueField = {
                fefoInfo: thresholds.info || 60,
                fefoWarning: thresholds.warning || 30,
                fefoCritical: thresholds.critical || 14,
            };
        }
        else if (rule.code === 'DEFAULT_LOCATION') {
            // value might be an object { id: ... } or a string null
            let locId = null;
            if (rule.value && typeof rule.value === 'object' && rule.value.id) locId = rule.value.id;
            else if (typeof rule.value === 'string') locId = rule.value;
            valueField = { locationId: locId };
        }
        else if (typeof rule.value === 'boolean') {
            valueField = { valueBoolean: rule.value };
        } else if (typeof rule.value === 'number') {
            valueField = { valueNumber: rule.value };
        } else {
            let strVal = rule.value === null ? '' : rule.value;
            if (typeof strVal === 'object') {
                strVal = strVal.id ? strVal.id : JSON.stringify(strVal);
            }
            valueField = { valueString: String(strVal) };
        }

        form.setFieldsValue({
            description: rule.description,
            isActive: rule.isActive,
            ...valueField
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();

        let parsedValue = editingRule.value;

        if (editingRule.code === 'FEFO_THRESHOLDS') {
            parsedValue = {
                info: Number(values.fefoInfo),
                warning: Number(values.fefoWarning),
                critical: Number(values.fefoCritical)
            };
        } else if (editingRule.code === 'DEFAULT_LOCATION') {
            parsedValue = values.locationId ? { id: values.locationId } : null;
        } else if (typeof editingRule.value === 'boolean') {
            parsedValue = values.valueBoolean;
        } else if (typeof editingRule.value === 'number') {
            parsedValue = Number(values.valueNumber);
        } else {
            if (editingRule.value !== null && typeof editingRule.value === 'object' && editingRule.value.id !== undefined) {
                parsedValue = { id: values.valueString };
            } else {
                parsedValue = values.valueString;
            }
        }

        const data = {
            description: values.description,
            isActive: values.isActive,
            value: parsedValue,
        };

        if (editingRule) {
            updateMutation.mutate({ id: editingRule.id, data });
        }
    };

    return (
        <div>
            <Card
                title={<Space><SafetyOutlined /> Reguły biznesowe</Space>}
                loading={isLoading}
            >
                <Table
                    dataSource={rules || []}
                    rowKey="id"
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 'max-content' }}
                    columns={[
                        { title: 'Kategoria', dataIndex: 'category', key: 'category' },
                        { title: 'Reguła', dataIndex: 'name', key: 'name' },
                        { title: 'Wartość', dataIndex: 'value', key: 'value', render: (val: any) => typeof val === 'boolean' ? (val ? <Tag color="green">Tak</Tag> : <Tag color="red">Nie</Tag>) : <Tag color="blue">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</Tag> },
                        { title: 'Opis', dataIndex: 'description', key: 'description' },
                        {
                            title: 'Status',
                            key: 'isActive',
                            render: (_: any, record: any) => (
                                <Switch
                                    checked={record.isActive}
                                    checkedChildren="AKTYWNA"
                                    unCheckedChildren="NIEAKTYWNA"
                                    onChange={(checked) => updateMutation.mutate({ id: record.id, data: { isActive: checked } })}
                                />
                            )
                        },
                        {
                            title: 'Edycja wartości', key: 'actions', render: (_: any, record: any) => (
                                <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>Edytuj testowo</Button>
                            )
                        },
                    ]}
                />
            </Card>

            <Modal
                title={`Edytuj wartość reguły: ${editingRule?.name}`}
                open={isModalOpen}
                onCancel={() => { setIsModalOpen(false); setEditingRule(null); form.resetFields(); }}
                onOk={handleSubmit}
                confirmLoading={updateMutation.isPending}
                okText="Zapisz"
                width={600}
            >
                <Form form={form} layout="vertical">
                    {editingRule?.code === 'FEFO_THRESHOLDS' ? (
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="fefoCritical" label="Krytyczny (dni)" rules={[{ required: true }]}>
                                    <Input type="number" addonAfter="dni" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="fefoWarning" label="Ostrzeżenie (dni)" rules={[{ required: true }]}>
                                    <Input type="number" addonAfter="dni" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="fefoInfo" label="Informacja (dni)" rules={[{ required: true }]}>
                                    <Input type="number" addonAfter="dni" />
                                </Form.Item>
                            </Col>
                        </Row>
                    ) : editingRule?.code === 'DEFAULT_LOCATION' ? (
                        <Form.Item name="locationId" label="Domyślna lokalizacja">
                            <Select allowClear placeholder="Wybierz lokalizację" showSearch optionFilterProp="label">
                                {locations.map(loc => (
                                    <Select.Option key={loc.id} value={loc.id} label={loc.name}>
                                        {loc.name} {loc.code ? `(${loc.code})` : ''}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <>
                            {editingRule && typeof editingRule.value === 'boolean' && (
                                <Form.Item name="valueBoolean" label="Wartość" valuePropName="checked">
                                    <Switch checkedChildren="TAK" unCheckedChildren="NIE" />
                                </Form.Item>
                            )}
                            {editingRule && typeof editingRule.value === 'number' && (
                                <Form.Item name="valueNumber" label="Wartość" rules={[{ required: true }]}>
                                    <Input type="number" />
                                </Form.Item>
                            )}
                            {editingRule && typeof editingRule.value !== 'boolean' && typeof editingRule.value !== 'number' && (
                                <Form.Item name="valueString" label="Wartość" rules={[{ required: true }]}>
                                    <Input placeholder="Wprowadź wartość reguły" />
                                </Form.Item>
                            )}
                        </>
                    )}
                    <Form.Item name="description" label="Aktualizacja opisu">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
